import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { toast } from "sonner";
import api from "../lib/api";
// Google sign-in intentionally removed — cross-site cookie is blocked by
// Safari ITP on arthursvault.com. Customers sign in with email + password.

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || "login");
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && f.password !== f.confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register({ first_name: f.first_name, last_name: f.last_name, email: f.email, password: f.password });
      toast.success(mode === "login" ? "Welcome back to the Vault" : "Vault account created");
      nav("/my-vault");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const forgot = async () => {
    if (!f.email) return toast.error("Enter your email first");
    await api.post("/auth/forgot", { email: f.email });
    toast.success("If an account exists, a reset link has been sent.");
  };

  return (
    <div>
      <Header/>
      <div className="max-w-md mx-auto pt-32 px-4 pb-24">
        <div className="vault-card p-8">
          <div className="mono-caption text-center">{mode === "login" ? "Enter the Vault" : "Unlock the Vault"}</div>
          <h1 className="font-serif text-3xl font-bold text-center mt-2 mb-6 gold-text">{mode === "login" ? "Sign In" : "Create Account"}</h1>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                <input required data-testid="first-name-input" placeholder="First name" className="input-vault" value={f.first_name} onChange={e=>setF({...f, first_name: e.target.value})}/>
                <input required data-testid="last-name-input" placeholder="Last name" className="input-vault" value={f.last_name} onChange={e=>setF({...f, last_name: e.target.value})}/>
              </div>
            )}
            <input required type="email" data-testid="email-input" placeholder="Email" className="input-vault" value={f.email} onChange={e=>setF({...f, email: e.target.value})}/>
            <div className="relative">
              <input required type={showPw ? "text" : "password"} data-testid="password-input" placeholder="Password" className="input-vault pr-11" value={f.password} onChange={e=>setF({...f, password: e.target.value})}/>
              <button
                type="button"
                data-testid="toggle-password"
                onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--av-text-dim)] hover:text-[var(--av-gold)] transition"
              >
                {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
            {mode === "register" && (
              <div className="relative">
                <input required type={showConfirm ? "text" : "password"} data-testid="confirm-input" placeholder="Confirm password" className="input-vault pr-11" value={f.confirm} onChange={e=>setF({...f, confirm: e.target.value})}/>
                <button
                  type="button"
                  data-testid="toggle-confirm"
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--av-text-dim)] hover:text-[var(--av-gold)] transition"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            )}
            <button disabled={busy} data-testid="auth-submit" className="btn-gold w-full">{busy ? "..." : (mode === "login" ? "Sign In" : "Create Account")}</button>
          </form>
          <div className="flex items-center justify-between mt-4 text-sm">
            <button data-testid="mode-toggle" onClick={()=>setMode(mode === "login" ? "register" : "login")} className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)]">{mode === "login" ? "Create account" : "Have account? Sign in"}</button>
            {mode === "login" && <button onClick={forgot} className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)]">Forgot password?</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
